import React, { useEffect, useRef, useState } from "react";
import "../../styles/components/CaseArticle.css";
import Modal from "../../components/Modal";
import ContactFormSimple from "../../components/ContactFormSimple";
import arrowCircle from "../../img/hero/arrow-circle-2.png";
import cloudsImage from "../../img/cases/case-clouds-3.webp";

/* тимчасові картинки для What We Built */
import builtIcon1 from "../../img/cases/built-icon-9.png";
import builtIcon2 from "../../img/cases/built-icon-16.png";
import builtIcon3 from "../../img/cases/built-icon-17.png";
import builtIcon4 from "../../img/cases/built-icon-3.png";


import stackIcon25 from "../../img/cases/stack-icon-25.png";
import stackIcon26 from "../../img/cases/stack-icon-26.png";
import stackIcon11 from "../../img/cases/stack-icon-11.png";
import stackIcon27 from "../../img/cases/stack-icon-27.png";
import stackIcon28 from "../../img/cases/stack-icon-28.png";
import stackIcon29 from "../../img/cases/stack-icon-29.png";
import stackIcon30 from "../../img/cases/stack-icon-30.png";
import stackIcon31 from "../../img/cases/stack-icon-31.png";

import IconPng from "../../components/IconPng";

import resultsBg1 from "../../img/cases/results-bg-1.png";
import resultsBg2 from "../../img/cases/results-bg-2.png";


const sections = [
  {
    id: "about",
    title: "About the Project",
    text: [
      "ChatShop transforms traditional eCommerce into a conversational experience. Instead of navigating filters and categories, users describe what they want in natural language — and the AI assistant finds, explains, and helps them purchase the right product. We built the full AI layer: from intent understanding and product retrieval to real-time recommendations.",
    ],
  },
  {
    id: "challenge",
    title: "The Challenge",
    text: [
      'Traditional eCommerce forces users through a rigid flow: browse categories, apply filters, read descriptions, compare. This works when you know exactly what you want — but fails for vague, exploratory, or context-dependent requests ("something for a gift", "a good beer for a BBQ").',
      "The challenge was building a system that understands these fuzzy requests, maps them to a real product catalog, and responds fast enough to feel like a conversation, not a search engine.",
    ],
  },
  {
    id: "goal",
    title: "The Goal",
    text: [
      "Replace static browsing with conversational product discovery. Increase conversion by reducing friction between intent and purchase.",
    ],
  },
];

const builtCards = [
  {
    id: "built-1",
    variant: "feature",
    title: "INTENT CLASSIFICATION LAYER",
    text:
      "Every user message is analyzed for intent type, product category, price filters, preferences, and conversational context. The system distinguishes between catalog search, recommendations, product comparisons, store/delivery questions, and follow-ups — routing each to a different processing flow. Combines LLM parsing with lightweight heuristics and conversational state.",
    icon: builtIcon1,
  },
  {
    id: "built-2",
    variant: "feature",
    title: "CUSTOM RAG PIPELINE",
    text:
      "Product catalog is synced via Odoo API, normalized, and enriched with semantic embeddings stored in PostgreSQL. Retrieval combines semantic search, structured filtering, category weighting, and business logic. Only the most relevant product context is passed to the LLM — minimizing latency, hallucinations, and token usage.",
    icon: builtIcon2,
  },
  {
    id: "built-3",
    variant: "feature",
    title: "LATENCY OPTIMIZATION",
    text:
      "For straightforward catalog searches, the system skips the second LLM generation stage entirely. If retrieval already returned relevant products, they're served directly as product blocks — making simple searches near real-time while preserving conversational UX for complex queries.",
    icon: builtIcon3,
  },
  {
    id: "built-4",
    variant: "feature",
    title: "SESSION CONTEXT & MEMORY",
    text:
      'Within a session, the system tracks previous intents, shown products, active filters, and preferences — enabling natural follow-ups ("something cheaper?", "show me more", "non-alcoholic?") without re-explaining context. For authorized users — persistent recommendation profiles, quiz results, favorites, and behavioral preferences. The system also ingests user data from ad campaigns and traffic sources, so the AI already has context before the first message. With each conversation, the assistant learns more about the buyer — building a progressively richer understanding that makes every next interaction more relevant.',
    icon: builtIcon4,
  },
];
const techStack = [
  {
    title: "GPT-4o-mini",
    icon: stackIcon25,
  },
  {
    title: "custom RAG orchestration",
    icon: stackIcon26,
  },
  {
    title: "PostgreSQL + semantic embeddings",
    icon: stackIcon11,
  },
  {
    title: "Node.js/Express",
    icon: stackIcon27,
    iconSize: "large" // 👈 додали
  },
  {
    title: "React",
    icon: stackIcon28,
  },
  {
    title: "Odoo API",
    icon: stackIcon29,
  },
  {
    title: "Firebase",
    icon: stackIcon30,
  },
  {
    title: "Amplitude",
    icon: stackIcon31,
  },
];

const resultsCards = [
  {
    variant: "small",
    icons: ["sms", "Component14"],
    text:
      "Fully functional conversational commerce platform",
  },
  {
    variant: "soft",
    icons: ["Component15", "Component16"],
    text:
      "Near real-time product search for direct queries",
    bg: resultsBg1,
  },
  {
    variant: "wide",
    icons: ["Database", "ArrowsClockwise-2"],
    text:
      "Automated catalog sync and semantic indexing",
  },
  {
    variant: "tall",
    icons: ["mozg", "qwertyz"],
    text:
      "Natural follow-up conversations without context loss",
    bg: resultsBg2,
  },
];

const beforeAfterCards = [
  {
    variant: "before",
    title: "Before",
    text:
      "Static catalog, manual filtering, zero personalization, high friction for users who don't know exactly what they want.",
  },
  {
    variant: "after",
    title: "After",
    text:
      "Natural language shopping — users describe what they need, AI finds it, explains it, and guides them to purchase. Vague requests are handled as naturally as specific ones.",
  },
];

const navItems = [
  ["about", "About the Project"],
  ["challenge", "The Challenge"],
  ["goal", "The Goal"],
  ["built", "What We Built"],
  ["key", "Key Challenge"],
  ["stack", "Tech Stack"],
  ["results", "Results"],
  ["before", "Before / After"],
  ["unique", "What Makes It Unique"],
];



export default function CaseArticleChatshop() {
  const [activeId, setActiveId] = useState("about");
  const [openModal, setOpenModal] = useState(false);
  const isManualScrolling = useRef(false);
  const scrollTimer = useRef(null);

  useEffect(() => {
    const elements = navItems
      .map(([id]) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);

        if (visibleEntries.length > 0) {
          if (isManualScrolling.current) return;
          const closest = visibleEntries.sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          )[0];



          setActiveId(closest.target.id);
        }
      },
      {
        root: null,
        rootMargin: "-25% 0px -60% 0px",
        threshold: 0,
      }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const items = document.querySelectorAll(".case-reveal-right");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -80px 0px",
      }
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();

    isManualScrolling.current = true;
    setActiveId(id);

    const section = document.getElementById(id);

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    clearTimeout(scrollTimer.current);

    scrollTimer.current = setTimeout(() => {
      isManualScrolling.current = false;
    }, 900);
  };

  return (
    <section className="case-article">
      <aside className="case-article__sidebar">
        <div className="case-article__toc">
          <h3>Contents</h3>

          <nav>
            {navItems.map(([id, label], index) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={(e) => handleNavClick(e, id)}
                className={
                  activeId === id
                    ? "case-article__toc-link active"
                    : "case-article__toc-link"
                }
              >
                <span className="case-article__toc-line" />
                <span className="case-article__toc-number">{index + 1}</span>
                <span className="case-article__toc-label">{label}</span>
              </a>
            ))}
          </nav>
        </div>

        <div className="case-article__cta">
          <h3>
            Ready to make your product
            <br />
            personal? Let’s talk!
          </h3>

          <button
            type="button"
            className="case-article__cta-btn"
            onClick={() => setOpenModal(true)}
          >
            <span>Contact Us</span>
            <img src={arrowCircle} alt="" aria-hidden="true" />
          </button>

          <img
            className="case-article__cta-clouds"
            src={cloudsImage}
            alt=""
            aria-hidden="true"
          />
        </div>
      </aside>

      <div className="case-article__content">
        {sections.map((section) => (
          <section
            key={section.id}
            id={section.id}
            className="case-article__section case-reveal-right"
          >
            <h2>{section.title}</h2>

            {section.text.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </section>
        ))}

        <section id="built" className="case-article__section">
          <h2>What We Built</h2>

          <div className="case-built-list">
            {builtCards.map((card) => (
              <article
                className={`case-built-card case-built-card--${card.variant} case-reveal-right`}
                key={card.id}
              >
                <img
                  className="case-built-card__icon"
                  src={card.icon}
                  alt=""
                  aria-hidden="true"
                />

                <div className="case-built-card__text">
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </div>

                {card.variant !== "feature" && card.image && (
                  <img
                    className="case-built-card__image"
                    src={card.image}
                    alt=""
                  />
                )}
              </article>
            ))}
          </div>
        </section>
        <section id="key" className="case-article__section case-reveal-right">
          <h2>Key Challenge</h2>

          <p>
            Handling vague, context-dependent queries at production speed. "Something dark to pair with meat" needs
            to be parsed into category (dark beer), context (food pairing), and intent (recommendation) — then matched
            against a real catalog with business logic, not just semantic similarity.
          </p>

          <p>
            The solution: a multi-stage pipeline where intent classification routes to the right retrieval strategy, and
            an optimization layer decides whether a full LLM response is even needed. This keeps simple searches fast and
            complex recommendations smart.
          </p>
        </section>
        <section id="stack" className="case-article__section case-reveal-right">
          <div className="case-stack">

            <h2 className="case-stack__title">Tech Stack</h2>

            <div className="case-stack__grid">
              {techStack.map((item) => (
                <div
                  key={item.title}
                  className={`case-stack__card ${item.size || ""}`}
                >
                  <img
                    src={item.icon}
                    alt=""
                    className={item.iconSize || ""}
                  />
                  <span>{item.title}</span>
                </div>
              ))}
            </div>

          </div>
        </section>

        <section id="results" className="case-article__section case-reveal-right">
          <h2>Results</h2>

          <div className="case-results">
            {resultsCards.map((card, index) => (
              <article
                key={index}
                className={`case-results__card case-results__card--${card.variant}`}
              >
                {card.bg && (
                  <img
                    className="case-results__bg"
                    src={card.bg}
                    alt=""
                    aria-hidden="true"
                  />
                )}

                <div className="case-results__icons">
                  {card.icons.map((icon) => (
                    <span className="case-results__icon" key={icon}>
                      <IconPng name={icon} size={28} />
                    </span>
                  ))}
                </div>

                {card.title && <h3>{card.title}</h3>}
                {card.text && <p>{card.text}</p>}
              </article>
            ))}
          </div>
        </section>

        <section id="before" className="case-article__section case-reveal-right">
          <div className="case-before-after">
            {beforeAfterCards.map((card) => (
              <article
                key={card.variant}
                className={`case-before-after__card case-before-after__card--${card.variant}`}
              >
                <h2>{card.title}</h2>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="unique" className="case-article__section case-reveal-right">
          <h2>What Makes It Unique</h2>

          <p>
            Not a chatbot bolted onto a store — a purpose-built conversational commerce engine that gets smarter with
            every interaction. User data flows in from ads, previous sessions, and in-conversation behavior, so the AI
            accumulates context over time instead of starting from zero. The intent classification + smart latency optimization
            means the system knows when to think (complex recommendations) and when to just answer (simple search). The RAG
            pipeline combines semantic understanding with real catalog business logic, so recommendations make sense both
            contextually and commercially.
          </p>
        </section>

      </div>

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title="Start your project"
      >
        <ContactFormSimple onSubmit={() => setOpenModal(false)} />
      </Modal>
    </section>
  );
}
